import { useEffect, useState } from 'react';
import { Keyboard, KeyboardStyle } from '@capacitor/keyboard';
import { Capacitor } from '@capacitor/core';
import { MobileKeyboardAvoidance } from '../util/responsiveUtils';

/**
 * Detects the current theme mode (dark or light)
 * Checks app theme first, then falls back to system preference
 */
function getCurrentThemeMode(): 'light' | 'dark' {
    // Check app theme via Material-UI's data attribute
    const htmlElement = document.documentElement;
    const muiColorScheme = htmlElement.getAttribute('data-mui-color-scheme');
    
    if (muiColorScheme === 'dark' || muiColorScheme === 'light') {
        return muiColorScheme;
    }
    
    // Fall back to system preference
    if (typeof window !== 'undefined' && window.matchMedia) {
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        return prefersDark ? 'dark' : 'light';
    }
    
    // Default to light
    return 'light';
}

const SCROLL_MARGIN_PX = 30;

/**
 * Visible viewport bottom in CSS pixels. Prefer Visual Viewport API so behavior matches on
 * iOS (resize: body) and Android (WebView content resized): getBoundingClientRect is relative
 * to the visual viewport, so we must not subtract plugin keyboard height again when the layout
 * already excludes the IME.
 */
function getVisibleViewportBottom(keyboardHeightFallback: number): number {
    const vv = window.visualViewport;
    const fromInset = window.innerHeight - keyboardHeightFallback;
    if (vv && vv.height > 0) {
        return Math.min(vv.offsetTop + vv.height, fromInset);
    }
    return fromInset;
}

function getVisibleViewportTop(): number {
    return window.visualViewport?.offsetTop ?? 0;
}

/**
 * Scrolls an input element into view, ensuring it stays above the keyboard.
 * Used on both native (Capacitor) and mobile web for consistent behavior.
 */
function scrollInputIntoView(element: HTMLElement, keyboardHeight: number) {
    try {
        // Android WebView uses adjustResize — layout already clears the IME. Global scrollIntoView /
        // window.scrollBy would move #root and feel like the page "jumps" under keyboard modals.
        if (
            Capacitor.isNativePlatform() &&
            Capacitor.getPlatform() === 'android' &&
            element.closest('.modal-with-inputs')
        ) {
            return;
        }

        const rect = element.getBoundingClientRect();
        const visibleBottom = getVisibleViewportBottom(keyboardHeight);
        const visibleTop = getVisibleViewportTop();

        const inputBottom = rect.bottom;
        const inputTop = rect.top;

        if (inputBottom > visibleBottom - SCROLL_MARGIN_PX) {
            const scrollAmount = inputBottom - (visibleBottom - SCROLL_MARGIN_PX);

            let scrollableParent: HTMLElement | null = element.parentElement;
            while (scrollableParent && scrollableParent !== document.body) {
                const style = window.getComputedStyle(scrollableParent);
                if (style.overflowY === 'auto' || style.overflowY === 'scroll' ||
                    style.overflow === 'auto' || style.overflow === 'scroll') {
                    break;
                }
                scrollableParent = scrollableParent.parentElement;
            }

            element.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
                inline: 'nearest'
            });

            if (scrollableParent) {
                scrollableParent.scrollBy({
                    top: scrollAmount,
                    behavior: 'smooth'
                });
            } else {
                window.scrollBy({
                    top: scrollAmount,
                    behavior: 'smooth'
                });
            }
        } else if (inputTop < visibleTop + SCROLL_MARGIN_PX) {
            element.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
                inline: 'nearest'
            });
        }
    } catch (error) {
        console.error('Error scrolling input into view:', error);
    }
}

/**
 * CSS inset for `.modal-with-inputs` / ChatModal. iOS uses resize: "body" (full layout viewport);
 * Android uses adjustResize so the web layout bottom is already above the IME — the same px inset
 * would double-offset (modals sit too high or scroll math is wrong).
 */
function modalKeyboardInsetBottomCss(pluginKeyboardHeight: number): string {
    if (Capacitor.getPlatform() === 'android') {
        return '0px';
    }
    return `${pluginKeyboardHeight}px`;
}

/**
 * Updates the keyboard style based on the current theme
 * Note: This only works on iOS. Android keyboards follow system theme automatically.
 */
async function updateKeyboardStyle(mode: 'light' | 'dark') {
    try {
        // Only set style on iOS (Android follows system theme automatically)
        if (Capacitor.getPlatform() === 'ios') {
            const style = mode === 'dark' ? KeyboardStyle.Dark : KeyboardStyle.Light;
            await Keyboard.setStyle({ style });
        }
    } catch (error) {
        console.error('Error setting keyboard style:', error);
    }
}

/**
 * Hook to handle keyboard avoidance on mobile devices.
 * Automatically scrolls focused input fields into view when keyboard appears.
 * Works for both iOS and Android.
 * Also syncs keyboard style with app theme (dark/light).
 */
export function useKeyboardAvoidance() {
    const [themeMode, setThemeMode] = useState<'light' | 'dark'>(getCurrentThemeMode);

    useEffect(() => {
        // Only run on native mobile platforms
        if (!Capacitor.isNativePlatform()) {
            return;
        }

        document.documentElement.setAttribute('data-capacitor-platform', Capacitor.getPlatform());

        // Initialize keyboard style based on current theme
        updateKeyboardStyle(themeMode);

        let keyboardHeight = 0;
        let keyboardShowListener: { remove: () => Promise<void> } | undefined;
        let keyboardHideListener: { remove: () => Promise<void> } | undefined;
        let keyboardDidShowListener: { remove: () => Promise<void> } | undefined;
        let keyboardDidHideListener: { remove: () => Promise<void> } | undefined;
        
        // Watch for theme changes via MutationObserver (for Material-UI theme changes)
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'attributes' && mutation.attributeName === 'data-mui-color-scheme') {
                    const newMode = getCurrentThemeMode();
                    setThemeMode((currentMode) => {
                        if (newMode !== currentMode) {
                            updateKeyboardStyle(newMode);
                            return newMode;
                        }
                        return currentMode;
                    });
                }
            });
        });

        // Observe the HTML element for theme changes
        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['data-mui-color-scheme']
        });

        // Also watch for system theme changes
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const handleSystemThemeChange = (e: MediaQueryListEvent) => {
            // Only update if app theme hasn't been explicitly set
            const htmlElement = document.documentElement;
            const muiColorScheme = htmlElement.getAttribute('data-mui-color-scheme');
            
            // If no explicit theme is set, use system preference
            if (!muiColorScheme) {
                const newMode = e.matches ? 'dark' : 'light';
                setThemeMode((currentMode) => {
                    if (newMode !== currentMode) {
                        updateKeyboardStyle(newMode);
                        return newMode;
                    }
                    return currentMode;
                });
            }
        };

        // Listen for system theme changes
        if (mediaQuery.addEventListener) {
            mediaQuery.addEventListener('change', handleSystemThemeChange);
        } else {
            // Fallback for older browsers
            mediaQuery.addListener(handleSystemThemeChange);
        }

        // Handle keyboard show event
        // NOTE: capacitor.config uses Keyboard resize: "body" on iOS; Android uses
        // windowSoftInputMode adjustResize (not resizeOnFullScreen — avoids cold-start layout bug).
        // We never set --keyboard-height on Paper (stays 0).
        const handleKeyboardShow = (info: { keyboardHeight: number }) => {
            keyboardHeight = info.keyboardHeight;
            // iOS only: `--keyboard-inset-bottom` is non-zero there; CSS repositions `.modal-with-inputs`.
            // On Android, inset stays 0 (adjustResize); setting this attribute still applied bottom-pin
            // rules and made the auth modal jump from center as if the page scrolled.
            if (Capacitor.getPlatform() !== 'android') {
                document.documentElement.setAttribute('data-keyboard-visible', 'true');
            }
            document.documentElement.style.setProperty(
                '--keyboard-inset-bottom',
                modalKeyboardInsetBottomCss(keyboardHeight)
            );

            // Find the currently focused input element
            const activeElement = document.activeElement as HTMLElement;

            if (activeElement && (
                activeElement.tagName === 'INPUT' ||
                activeElement.tagName === 'TEXTAREA' ||
                activeElement.contentEditable === 'true'
            )) {
                requestAnimationFrame(() => {
                    scrollInputIntoView(activeElement, keyboardHeight);
                });
            }
        };

        // Handle keyboard hide event
        const handleKeyboardHide = () => {
            keyboardHeight = 0;
            document.documentElement.removeAttribute('data-keyboard-visible');
            document.documentElement.style.setProperty('--keyboard-inset-bottom', '0px');
        };

        // Listen for focus events on input elements
        const handleInputFocus = (event: FocusEvent) => {
            const target = event.target as HTMLElement;
            
            if (target && (
                target.tagName === 'INPUT' ||
                target.tagName === 'TEXTAREA' ||
                target.contentEditable === 'true'
            )) {
                // If keyboard is already shown, scroll immediately
                if (keyboardHeight > 0) {
                    setTimeout(() => {
                        scrollInputIntoView(target, keyboardHeight);
                    }, 150);
                } else {
                    // Keyboard might not be shown yet, wait a bit then scroll
                    setTimeout(() => {
                        // Check keyboard height again (might have changed)
                        const currentKbHeight = keyboardHeight || 0;
                        if (currentKbHeight > 0) {
                            scrollInputIntoView(target, currentKbHeight);
                        }
                    }, 300);
                }
            }
        };

        // Initialize keyboard listeners (Android: also didShow/didHide — some devices/timings are more reliable)
        const initializeKeyboardListeners = async () => {
            try {
                keyboardShowListener = await Keyboard.addListener('keyboardWillShow', handleKeyboardShow);
                keyboardHideListener = await Keyboard.addListener('keyboardWillHide', handleKeyboardHide);
                if (Capacitor.getPlatform() === 'android') {
                    keyboardDidShowListener = await Keyboard.addListener('keyboardDidShow', handleKeyboardShow);
                    keyboardDidHideListener = await Keyboard.addListener('keyboardDidHide', handleKeyboardHide);
                }
            } catch (error) {
                console.error('Error setting up keyboard listeners:', error);
            }
        };

        // Initialize listeners
        initializeKeyboardListeners();
        document.addEventListener('focusin', handleInputFocus);

        // Cleanup
        return () => {
            document.documentElement.removeAttribute('data-capacitor-platform');
            observer.disconnect();
            if (mediaQuery.removeEventListener) {
                mediaQuery.removeEventListener('change', handleSystemThemeChange);
            } else {
                mediaQuery.removeListener(handleSystemThemeChange);
            }
            void keyboardShowListener?.remove();
            void keyboardHideListener?.remove();
            void keyboardDidShowListener?.remove();
            void keyboardDidHideListener?.remove();
            document.removeEventListener('focusin', handleInputFocus);
        };
    }, [themeMode]);

    // Mobile web: same behavior as native app – do NOT shrink modals (no --keyboard-height),
    // and scroll focused input into view when keyboard is detected via visualViewport.
    useEffect(() => {
        if (Capacitor.isNativePlatform()) return;

        const vv = window.visualViewport;
        if (!vv) return;

        const mq = window.matchMedia('(max-width: 768px), (max-height: 768px)');
        if (!mq.matches) return;

        const KEYBOARD_THRESHOLD = 150; // px – ignore small viewport changes (e.g. URL bar)
        let keyboardHeight = 0;
        /** Largest `innerHeight` seen while the keyboard was treated as hidden — Android Chrome often shrinks layout height instead of only visualViewport. */
        let peakInnerHeight = window.innerHeight;

        const isAndroidMobileWeb = /Android/i.test(navigator.userAgent);

        /** Obscured layout height from below (IME), including visual viewport offset when the browser shifts it. */
        const obscuredBottomFromVisualViewport = (): number =>
            Math.max(0, Math.round(window.innerHeight - vv.offsetTop - vv.height));

        /**
         * Caps absurd inset values so `.modal-with-inputs` max-height never collapses to a sliver
         * when innerHeight/offsetTop briefly misreport.
         */
        const clampKeyboardInset = (raw: number): number => {
            // Cap against peak layout height, not current innerHeight (which may already be shrunk with the keyboard open).
            const cap = Math.max(
                KEYBOARD_THRESHOLD + 1,
                peakInnerHeight - MobileKeyboardAvoidance.MIN_LAYOUT_HEIGHT_FOR_MODAL_PX
            );
            return Math.max(0, Math.min(Math.round(raw), cap));
        };

        const applyKeyboardInset = () => {
            const innerH = window.innerHeight;
            const vvObscured = obscuredBottomFromVisualViewport();
            const shrinkObscured = Math.max(0, Math.round(peakInnerHeight - innerH));
            let raw = Math.max(vvObscured, shrinkObscured);

            if (raw > KEYBOARD_THRESHOLD && isAndroidMobileWeb) {
                raw += MobileKeyboardAvoidance.ANDROID_CREDENTIAL_STRIP_EXTRA_PX;
            }
            const inset = clampKeyboardInset(raw);

            if (raw > KEYBOARD_THRESHOLD) {
                keyboardHeight = inset;
                document.documentElement.setAttribute('data-keyboard-visible', 'true');
                document.documentElement.style.setProperty('--keyboard-inset-bottom', `${keyboardHeight}px`);
                const active = document.activeElement as HTMLElement;
                if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA' || active.contentEditable === 'true')) {
                    requestAnimationFrame(() => scrollInputIntoView(active, keyboardHeight));
                }
            } else {
                keyboardHeight = 0;
                document.documentElement.removeAttribute('data-keyboard-visible');
                document.documentElement.style.setProperty('--keyboard-inset-bottom', '0px');
                peakInnerHeight = Math.max(peakInnerHeight, innerH);
            }
        };

        const handleFocusIn = (e: FocusEvent) => {
            const target = e.target as HTMLElement;
            if (!target || (target.tagName !== 'INPUT' && target.tagName !== 'TEXTAREA' && target.contentEditable !== 'true')) return;
            // Android often applies innerHeight / visualViewport changes after focus — re-measure a few times.
            const remeasureDelays = [0, 80, 200, 450];
            remeasureDelays.forEach((ms) => {
                window.setTimeout(() => applyKeyboardInset(), ms);
            });
            if (keyboardHeight > 0) {
                setTimeout(() => scrollInputIntoView(target, keyboardHeight), 150);
            } else {
                setTimeout(() => {
                    if (keyboardHeight > 0) scrollInputIntoView(target, keyboardHeight);
                }, 300);
            }
        };

        vv.addEventListener('resize', applyKeyboardInset);
        vv.addEventListener('scroll', applyKeyboardInset);
        window.addEventListener('resize', applyKeyboardInset);
        document.addEventListener('focusin', handleFocusIn);
        applyKeyboardInset();

        return () => {
            vv.removeEventListener('resize', applyKeyboardInset);
            vv.removeEventListener('scroll', applyKeyboardInset);
            window.removeEventListener('resize', applyKeyboardInset);
            document.removeEventListener('focusin', handleFocusIn);
            document.documentElement.removeAttribute('data-keyboard-visible');
            document.documentElement.style.setProperty('--keyboard-inset-bottom', '0px');
        };
    }, []);
}
